import { useMemo } from "react";
import type { SessionLog } from "../../lib/types";

interface LearningCalendarProps {
  logs: SessionLog[];
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const WEEKDAY_HEADER = ["一", "二", "三", "四", "五", "六", "日"];

export function LearningCalendarWidget({ logs }: LearningCalendarProps) {
  const { monthLabel, yearLabel, calendarGrid } = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const todayDate = now.getDate();

    // First day of current month
    const firstDay = new Date(currentYear, currentMonth, 1);
    // 0 = Sunday, 1 = Monday ... convert so Monday = 0, Sunday = 6
    const firstDayWeekday = (firstDay.getDay() + 6) % 7;

    // Number of days in current month
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

    // Days in previous month
    const daysInPrevMonth = new Date(currentYear, currentMonth, 0).getDate();

    const grid = [];

    // Prefix days from previous month
    for (let i = firstDayWeekday - 1; i >= 0; i--) {
      grid.push({
        dayNumber: daysInPrevMonth - i,
        isCurrentMonth: false,
        isToday: false,
        hasActivity: false,
      });
    }

    // Days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const isToday = day === todayDate;
      const monthStr = String(currentMonth + 1).padStart(2, "0");
      const dayStr = String(day).padStart(2, "0");
      const iso = `${currentYear}-${monthStr}-${dayStr}`;
      const hasActivity = logs.some((l) => l.date.slice(0, 10) === iso && l.done > 0);

      grid.push({
        dayNumber: day,
        isCurrentMonth: true,
        isToday,
        hasActivity,
      });
    }

    // Suffix days to complete the last row (up to multiple of 7)
    const remainingSlots = (7 - (grid.length % 7)) % 7;
    for (let i = 1; i <= remainingSlots; i++) {
      grid.push({
        dayNumber: i,
        isCurrentMonth: false,
        isToday: false,
        hasActivity: false,
      });
    }

    return {
      monthLabel: MONTH_NAMES[currentMonth],
      yearLabel: currentYear,
      calendarGrid: grid,
    };
  }, [logs]);

  return (
    <div className="rounded-2xl bg-white border border-slate-100/90 p-4 sm:p-5 shadow-xs w-full min-w-0 overflow-hidden">
      <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2 min-w-0">
        <span className="text-[13.5px] sm:text-[14px] font-bold text-slate-900 truncate">學習日曆</span>
        <span className="text-[11px] sm:text-[12px] font-grotesk font-medium text-slate-400 truncate">
          {monthLabel} {yearLabel}
        </span>
      </div>

      <div className="grid grid-cols-7 gap-y-1 sm:gap-y-2 text-center w-full min-w-0">
        {WEEKDAY_HEADER.map((w) => (
          <div key={w} className="text-[10.5px] sm:text-[12px] font-medium text-slate-400 py-0.5">
            {w}
          </div>
        ))}

        {calendarGrid.map((cell, idx) => (
          <div key={idx} className="flex flex-col items-center justify-center h-6 sm:h-7 min-w-0">
            {cell.isToday ? (
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[#2563EB] text-white font-bold text-[11px] sm:text-[12px] flex items-center justify-center shadow-xs">
                {cell.dayNumber}
              </div>
            ) : cell.isCurrentMonth ? (
              <div className="relative group w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center">
                <span className="text-[11px] sm:text-[12px] font-medium text-slate-700">
                  {cell.dayNumber}
                </span>
                {cell.hasActivity && (
                  <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-blue-500" />
                )}
              </div>
            ) : (
              <span className="text-[11px] sm:text-[12px] text-slate-300 font-normal">
                {cell.dayNumber}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
